<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content">
        <pageheader title="Damaged Stock" title1="Damaged and expired stock kept aside, and what happened to it" />

        <!-- Summary cards double as status filters -->
        <div class="ds-cards">
          <button
            v-for="card in cards"
            :key="card.status"
            type="button"
            class="ds-card"
            :class="[`ds-card-${card.tone}`, { active: status === card.status }]"
            @click="setStatus(status === card.status ? '' : card.status)"
          >
            <span class="ds-card-label"><i :class="card.icon"></i> {{ card.label }}</span>
            <strong>{{ formatQty(summary[card.status].quantity) }} <small>units</small></strong>
            <span class="ds-card-sub">{{ summary[card.status].count }} {{ summary[card.status].count === 1 ? "entry" : "entries" }} &middot; {{ money(summary[card.status].value) }}</span>
          </button>
        </div>

        <div class="card">
          <div class="card-body">
            <div class="ds-toolbar">
              <div class="ds-search">
                <i class="fa fa-search" aria-hidden="true"></i>
                <input v-model.trim="search" type="search" class="form-control" placeholder="Search item, batch or reason..." @input="debouncedLoad" />
              </div>
              <select v-model="source" class="form-control ds-select" @change="reload">
                <option value="">All sources</option>
                <option value="SALES_RETURN">Sales returns</option>
                <option value="WRITE_OFF">Stock adjustments</option>
              </select>
              <input v-model="dateFrom" type="date" class="form-control ds-date" title="Recorded from" @change="reload" />
              <input v-model="dateTo" type="date" class="form-control ds-date" title="Recorded to" @change="reload" />
              <button v-if="hasFilters" type="button" class="ds-clear" @click="clearFilters">Clear filters</button>
            </div>

            <div v-if="selected.length" class="ds-bulk">
              <strong>{{ selected.length }} selected</strong>
              <span class="muted">Mark as:</span>
              <button type="button" class="ds-btn ds-btn-vendor" @click="openAction(selected, 'RETURNED_TO_VENDOR')"><i class="fas fa-truck"></i> Returned to supplier</button>
              <button type="button" class="ds-btn ds-btn-disposed" @click="openAction(selected, 'DISPOSED')"><i class="fas fa-trash-alt"></i> Disposed</button>
              <button type="button" class="ds-btn ds-btn-written" @click="openAction(selected, 'WRITTEN_OFF')"><i class="fas fa-file-invoice"></i> Written off</button>
              <button type="button" class="ds-link" @click="selected = []">Clear selection</button>
            </div>

            <div v-if="loading" class="ds-empty">Loading...</div>
            <div v-else-if="!rows.length" class="ds-empty">
              <i class="fas fa-box-open"></i>
              <strong>{{ hasFilters || status ? "Nothing matches these filters" : "No damaged stock" }}</strong>
              <span v-if="!hasFilters && !status">
                Items appear here when a sales return is marked <em>Damaged</em>, or when a stock adjustment removes damaged or expired stock.
              </span>
            </div>
            <div v-else class="table-responsive">
              <table class="ds-table">
                <thead>
                  <tr>
                    <th class="col-check">
                      <input
                        v-if="canManage && selectableIds.length"
                        type="checkbox"
                        :checked="allSelected"
                        :indeterminate="selected.length > 0 && !allSelected"
                        aria-label="Select all on this page"
                        @change="toggleAll($event.target.checked)"
                      />
                    </th>
                    <th>Item</th>
                    <th>From</th>
                    <th>Reason</th>
                    <th class="num">Qty</th>
                    <th class="num">Value</th>
                    <th>Status</th>
                    <th v-if="canManage" class="text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in rows" :key="row.id" :class="{ picked: selected.includes(row.id) }">
                    <td class="col-check">
                      <input
                        v-if="canManage && row.status !== 'CANCELLED'"
                        v-model="selected"
                        type="checkbox"
                        :value="row.id"
                        :aria-label="`Select ${row.itemname}`"
                      />
                    </td>
                    <td>
                      <div class="ds-item">{{ row.itemname }}</div>
                      <div class="ds-sub">
                        {{ row.itemcode }}
                        <template v-if="row.batchno"> &middot; Batch {{ row.batchno }}</template>
                        <template v-if="row.expirydate"> &middot; Exp {{ formatDate(row.expirydate) }}</template>
                      </div>
                    </td>
                    <td>
                      <router-link v-if="sourceLink(row)" :to="sourceLink(row)" class="ds-source">{{ row.sourceno || "Open" }}</router-link>
                      <span v-else>{{ row.sourceno || "-" }}</span>
                      <div class="ds-sub">{{ sourceLabel(row.sourcetype) }} &middot; {{ formatDate(row.createdat) }}</div>
                    </td>
                    <td class="ds-reason">{{ row.reason || "-" }}</td>
                    <td class="num">{{ formatQty(row.quantity) }} <small class="muted">{{ row.unit }}</small></td>
                    <td class="num">{{ money(row.value) }}</td>
                    <td>
                      <span class="ds-pill" :class="`pill-${row.status.toLowerCase()}`">{{ statusLabel(row.status) }}</span>
                      <div v-if="row.disposaldate && row.status !== 'PENDING'" class="ds-sub">{{ formatDate(row.disposaldate) }}</div>
                      <div v-if="row.disposalremarks" class="ds-sub ds-remark" :title="row.disposalremarks">{{ row.disposalremarks }}</div>
                    </td>
                    <td v-if="canManage" class="text-end ds-actions">
                      <template v-if="row.status === 'PENDING'">
                        <button type="button" class="ds-btn ds-btn-vendor" title="Returned to supplier" @click="openAction([row.id], 'RETURNED_TO_VENDOR')"><i class="fas fa-truck"></i><span> Supplier</span></button>
                        <button type="button" class="ds-btn ds-btn-disposed" title="Disposed" @click="openAction([row.id], 'DISPOSED')"><i class="fas fa-trash-alt"></i><span> Disposed</span></button>
                        <button type="button" class="ds-btn ds-btn-written" title="Written off" @click="openAction([row.id], 'WRITTEN_OFF')"><i class="fas fa-file-invoice"></i></button>
                      </template>
                      <button v-else-if="row.status !== 'CANCELLED'" type="button" class="ds-link" @click="openAction([row.id], 'PENDING')">Undo</button>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div class="ds-pager">
                <span>{{ rangeText }}</span>
                <div>
                  <button type="button" class="ds-page-btn" :disabled="page <= 1" @click="goPage(page - 1)">&lsaquo; Prev</button>
                  <button type="button" class="ds-page-btn" :disabled="page * pageSize >= total" @click="goPage(page + 1)">Next &rsaquo;</button>
                </div>
              </div>
            </div>
            <p class="ds-note"><i class="fas fa-info-circle"></i> Damaged stock is already out of sellable stock. Marking it here only records what happened to it; stock quantities do not change.</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";

const STATUS_LABELS = {
  PENDING: "Awaiting action",
  RETURNED_TO_VENDOR: "Returned to supplier",
  DISPOSED: "Disposed",
  WRITTEN_OFF: "Written off",
  CANCELLED: "Cancelled",
};

function today() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const emptySummary = () => Object.fromEntries(Object.keys(STATUS_LABELS).map((key) => [key, { count: 0, quantity: 0, value: 0 }]));

export default {
  name: "damagedstock",
  data() {
    return {
      rows: [],
      total: 0,
      page: 1,
      pageSize: 20,
      loading: false,
      summary: emptySummary(),
      status: "PENDING",
      source: "",
      search: "",
      dateFrom: "",
      dateTo: "",
      selected: [],
      searchTimer: null,
      cards: [
        { status: "PENDING", label: "Awaiting action", tone: "warn", icon: "fas fa-hourglass-half" },
        { status: "RETURNED_TO_VENDOR", label: "Returned to supplier", tone: "info", icon: "fas fa-truck" },
        { status: "DISPOSED", label: "Disposed", tone: "muted", icon: "fas fa-trash-alt" },
        { status: "WRITTEN_OFF", label: "Written off", tone: "bad", icon: "fas fa-file-invoice" },
      ],
    };
  },
  computed: {
    canManage() {
      return this.$store.getters.hasPermission("inventory.adjust");
    },
    hasFilters() {
      return Boolean(this.search || this.source || this.dateFrom || this.dateTo);
    },
    selectableIds() {
      return this.rows.filter((row) => row.status !== "CANCELLED").map((row) => row.id);
    },
    allSelected() {
      return this.selectableIds.length > 0 && this.selectableIds.every((id) => this.selected.includes(id));
    },
    rangeText() {
      if (!this.total) return "";
      const start = (this.page - 1) * this.pageSize + 1;
      return `${start}-${Math.min(this.page * this.pageSize, this.total)} of ${this.total}`;
    },
  },
  mounted() {
    this.load();
  },
  beforeUnmount() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const { data } = await axios.get("/v1/damaged-stock", {
          params: {
            page: this.page,
            page_size: this.pageSize,
            status: this.status || undefined,
            source: this.source || undefined,
            search: this.search || undefined,
            date_from: this.dateFrom || undefined,
            date_to: this.dateTo || undefined,
          },
        });
        this.rows = data.items || [];
        this.total = data.total || 0;
        this.summary = { ...emptySummary(), ...(data.summary || {}) };
        this.selected = this.selected.filter((id) => this.rows.some((row) => row.id === id));
      } catch (error) {
        this.rows = [];
        Swal.fire({ icon: "error", title: "Could not load damaged stock", text: error.response?.data?.detail || error.message });
      } finally {
        this.loading = false;
      }
    },
    reload() {
      this.page = 1;
      this.load();
    },
    debouncedLoad() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.reload, 300);
    },
    setStatus(value) {
      this.status = value;
      this.selected = [];
      this.reload();
    },
    clearFilters() {
      this.search = "";
      this.source = "";
      this.dateFrom = "";
      this.dateTo = "";
      this.reload();
    },
    goPage(page) {
      this.page = page;
      this.selected = [];
      this.load();
    },
    toggleAll(checked) {
      this.selected = checked ? [...this.selectableIds] : [];
    },
    async openAction(ids, status) {
      const reopening = status === "PENDING";
      const count = ids.length;
      const what = count === 1 ? "this entry" : `${count} entries`;
      const prompts = {
        RETURNED_TO_VENDOR: { title: `Returned to supplier`, placeholder: "e.g. Sent back with the delivery van; credit note expected" },
        DISPOSED: { title: "Disposed", placeholder: "e.g. Thrown away / destroyed" },
        WRITTEN_OFF: { title: "Written off", placeholder: "e.g. Loss booked for the month" },
        PENDING: { title: "Move back to awaiting action?", placeholder: "Reason (optional)" },
      };
      const prompt = prompts[status];
      const { value: form } = await Swal.fire({
        title: prompt.title,
        html: `
          <div class="text-start">
            <p style="margin:0 0 12px;color:#6b7280;font-size:14px;">${reopening ? `Undo the status of ${what}.` : `Mark ${what} as <strong>${STATUS_LABELS[status].toLowerCase()}</strong>.`}</p>
            ${reopening ? "" : `<label style="display:block;font-size:13px;font-weight:600;margin-bottom:4px;">Date</label>
            <input id="ds-date" type="date" class="swal2-input" value="${today()}" style="margin:0 0 12px;width:100%;" />`}
            <label style="display:block;font-size:13px;font-weight:600;margin-bottom:4px;">Remarks <span style="font-weight:400;color:#9ca3af;">(optional)</span></label>
            <input id="ds-remarks" type="text" maxlength="250" class="swal2-input" placeholder="${prompt.placeholder}" style="margin:0;width:100%;" />
          </div>`,
        showCancelButton: true,
        confirmButtonText: reopening ? "Undo" : "Save",
        focusConfirm: false,
        preConfirm: () => ({
          disposaldate: reopening ? null : document.getElementById("ds-date").value || null,
          remarks: document.getElementById("ds-remarks").value.trim() || null,
        }),
      });
      if (!form) return;
      try {
        await axios.patch("/v1/damaged-stock/status", { ids, status, ...form });
        Swal.fire({ toast: true, position: "top-end", icon: "success", title: reopening ? "Moved back to awaiting action" : `Marked as ${STATUS_LABELS[status].toLowerCase()}`, showConfirmButton: false, timer: 1600 });
        this.selected = [];
        this.load();
      } catch (error) {
        Swal.fire({ icon: "error", title: "Could not update", text: error.response?.data?.detail || error.message });
      }
    },
    sourceLink(row) {
      if (row.sourcetype === "SALES_RETURN") return { path: "/createsalesreturn", query: { id: row.sourceid } };
      if (row.sourcetype === "WRITE_OFF") return { path: "/stockadjustment", query: { id: row.sourceid } };
      return null;
    },
    sourceLabel(type) {
      return { SALES_RETURN: "Sales return", PURCHASE_RETURN: "Purchase return", WRITE_OFF: "Stock adjustment" }[type] || type;
    },
    statusLabel(status) {
      return STATUS_LABELS[status] || status;
    },
    formatDate(value) {
      if (!value) return "-";
      return new Date(value).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    },
    formatQty(value) {
      return Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
    },
    money(value) {
      return `Rs. ${Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    },
  },
};
</script>

<style scoped>
.ds-cards { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 14px; margin-bottom: 16px; }
.ds-card { display: flex; flex-direction: column; gap: 4px; text-align: left; padding: 14px 16px; border: 1.5px solid #e9ecf2; border-radius: 12px; background: #fff; transition: border-color 0.15s ease, box-shadow 0.15s ease; }
.ds-card:hover { border-color: #d1d5db; }
.ds-card.active { box-shadow: 0 0 0 3px rgba(255, 159, 67, 0.15); border-color: #FF9F43; }
.ds-card-label { font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; color: #6b7280; display: flex; align-items: center; gap: 6px; }
.ds-card strong { font-size: 22px; color: #111827; line-height: 1.2; }
.ds-card strong small { font-size: 12px; font-weight: 500; color: #6b7280; }
.ds-card-sub { font-size: 12px; color: #6b7280; }
.ds-card-warn .ds-card-label i { color: #f59e0b; }
.ds-card-info .ds-card-label i { color: #2563eb; }
.ds-card-muted .ds-card-label i { color: #6b7280; }
.ds-card-bad .ds-card-label i { color: #dc2626; }

.ds-toolbar { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 14px; }
.ds-search { position: relative; flex: 1 1 260px; max-width: 360px; }
.ds-search i { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #9ca3af; }
.ds-search input { padding-left: 34px; height: 38px; border-radius: 8px; }
.ds-select { width: 180px; height: 38px; border-radius: 8px; }
.ds-date { width: 160px; height: 38px; border-radius: 8px; }
.ds-clear { border: 0; background: #f3f4f6; border-radius: 8px; padding: 0 14px; height: 38px; font-size: 13px; font-weight: 600; color: #374151; }

.ds-bulk { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; padding: 10px 14px; margin-bottom: 12px; border: 1px solid #fed7aa; border-radius: 10px; background: #fffaf5; font-size: 13px; }
.muted { color: #9ca3af; }

.ds-table { width: 100%; border-collapse: collapse; }
.ds-table th, .ds-table td { padding: 11px 10px; border-bottom: 1px solid #f1f2f4; font-size: 13.5px; vertical-align: middle; color: #374151; }
.ds-table th { background: #f9fafb; color: #6b7280; font-size: 11.5px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; white-space: nowrap; }
.ds-table tbody tr:hover td { background: #fffaf5; }
.ds-table tbody tr.picked td { background: #fff4e8; }
.ds-table .num { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
.col-check { width: 36px; }
.col-check input { width: 16px; height: 16px; accent-color: #FF9F43; cursor: pointer; }
.ds-item { font-weight: 500; color: #111827; }
.ds-sub { font-size: 12px; color: #9ca3af; margin-top: 2px; }
.ds-remark { max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ds-reason { max-width: 220px; }
.ds-source { color: #c2410c; font-weight: 600; }
.ds-pill { display: inline-block; padding: 2px 10px; border-radius: 999px; font-size: 12px; font-weight: 600; white-space: nowrap; }
.pill-pending { background: #fef3c7; color: #b45309; }
.pill-returned_to_vendor { background: #dbeafe; color: #1d4ed8; }
.pill-disposed { background: #f3f4f6; color: #374151; }
.pill-written_off { background: #fee2e2; color: #b91c1c; }
.pill-cancelled { background: #f9fafb; color: #9ca3af; text-decoration: line-through; }

.ds-actions { white-space: nowrap; }
.ds-btn { border: 0; border-radius: 7px; padding: 5px 10px; margin-left: 4px; font-size: 12.5px; font-weight: 600; }
.ds-btn-vendor { background: #eff6ff; color: #1d4ed8; }
.ds-btn-disposed { background: #f3f4f6; color: #374151; }
.ds-btn-written { background: #fef2f2; color: #b91c1c; }
.ds-link { border: 0; background: transparent; color: #c2410c; font-size: 12.5px; font-weight: 600; padding: 0 4px; }
.ds-link:hover { text-decoration: underline; }

.ds-empty { display: flex; flex-direction: column; align-items: center; gap: 6px; padding: 48px 16px; color: #6b7280; text-align: center; font-size: 14px; }
.ds-empty i { font-size: 28px; color: #d1d5db; }
.ds-empty strong { color: #111827; font-size: 16px; }
.ds-pager { display: flex; align-items: center; justify-content: space-between; padding-top: 12px; font-size: 13px; color: #6b7280; }
.ds-page-btn { border: 1px solid #e5e7eb; background: #fff; border-radius: 7px; padding: 5px 12px; margin-left: 6px; font-size: 13px; }
.ds-page-btn:disabled { opacity: 0.5; }
.ds-note { margin: 14px 0 0; font-size: 12px; color: #9ca3af; }

@media (max-width: 1100px) {
  .ds-cards { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .ds-actions .ds-btn span { display: none; }
}
@media (max-width: 576px) {
  .ds-cards { grid-template-columns: 1fr; }
}
</style>
